import { useCallback, useEffect, useMemo, useState } from 'react';
import { Spin } from 'antd';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { SearchLoupeQuestion } from '@assets/icons/SearchLoupeQuestion';
import { UnstyledButton } from '@components/UnstyledButton/UnstyledButton';
import { useGetJobsBoardLocations } from '@hooks/jobsBoard/useGetJobsBoardLocations';
import { useGetJobsBoardLocationsPublic } from '@hooks/jobsBoard/useGetJobsBoardLocationsPublic';
import {
  buildProjectLocationOptions,
  getParsedProjectLocationFromJsonString,
  SelectOption,
} from '@hooks/jobsBoard/utils';
import { RootState } from '@lib/redux.lib/store';
import { useJobsBoardFiltersViewContext } from '@views/jobsBoard/context/JobsBoardFiltersViewContext';
import { useJobsBoardFiltersModalContext } from '@views/jobsBoard/JobsBoardSection/JobsBoardFilters/JobsBoardFiltersModal/context';

import { FilterCollapse } from '../components/FilterCollapse';
import { StyledCheckboxGroup, StyledInput } from '../styles';

export const ProjectLocationFilter = () => {
  const loggedInUserId = useSelector(
    (state: RootState) => state.app?.user?.profile?.id
  );

  const isLoggedIn = !!loggedInUserId;

  const { mainViewCountry } = useJobsBoardFiltersViewContext();
  const { debouncedProjectLocationSearchTerm } =
    useJobsBoardFiltersModalContext();

  if (isLoggedIn) {
    return (
      <ProjectLocationFilterWrapperPrivate
        debouncedProjectLocationSearchTerm={debouncedProjectLocationSearchTerm}
      />
    );
  }

  return (
    <ProjectLocationFilterWrapperPublic
      debouncedProjectLocationSearchTerm={debouncedProjectLocationSearchTerm}
      mainViewCountry={mainViewCountry}
    />
  );
};

type ProjectLocationFilterWrapperPublicProps = {
  mainViewCountry: ProductionCountry;
  debouncedProjectLocationSearchTerm: string;
};
const ProjectLocationFilterWrapperPublic = ({
  mainViewCountry,
  debouncedProjectLocationSearchTerm,
}: ProjectLocationFilterWrapperPublicProps) => {
  const {
    data: projectLocations,
    isLoading: isProjectLocationsLoading,
    isError: isProjectLocationsError,
  } = useGetJobsBoardLocationsPublic(
    debouncedProjectLocationSearchTerm,
    mainViewCountry
  );
  return (
    <ProjectLocationFilterSummary
      projectLocations={projectLocations}
      isProjectLocationsLoading={isProjectLocationsLoading}
      isProjectLocationsError={isProjectLocationsError}
    />
  );
};

const ProjectLocationFilterWrapperPrivate = ({
  debouncedProjectLocationSearchTerm,
}: {
  debouncedProjectLocationSearchTerm: string;
}) => {
  const {
    data: projectLocations,
    isLoading: isProjectLocationsLoading,
    isError: isProjectLocationsError,
  } = useGetJobsBoardLocations(debouncedProjectLocationSearchTerm);

  return (
    <ProjectLocationFilterSummary
      projectLocations={projectLocations}
      isProjectLocationsLoading={isProjectLocationsLoading}
      isProjectLocationsError={isProjectLocationsError}
    />
  );
};

type ProjectLocationFilterProps = {
  projectLocations: LocationModel[] | undefined;
  isProjectLocationsLoading: boolean;
  isProjectLocationsError: boolean;
};
const ProjectLocationFilterSummary = ({
  projectLocations,
  isProjectLocationsLoading,
  isProjectLocationsError,
}: ProjectLocationFilterProps) => {
  const {
    projectLocationSearchTerm,
    setModalFilters,
    modalFilters,
    setCityStateStringList,
    setProjectLocationSearchTerm,
    onClearProjectLocationSearchTerm,
  } = useJobsBoardFiltersModalContext();
  const options = useMemo(
    () => buildProjectLocationOptions(projectLocations),
    [projectLocations]
  );

  useEffect(() => {
    if (isProjectLocationsLoading) {
      return;
    }
    setModalFilters((prev) => {
      const { cityStateStringList: rawCityStateStringList, ...rest } =
        prev || {};

      const currentCityStateStringList = rawCityStateStringList || [];

      const newCityStateStringList =
        options?.reduce((acc, { value }) => {
          if (currentCityStateStringList?.includes(value) && value) {
            acc.push(value);
          }

          return acc;
        }, [] as string[]) || ([] as string[]);

      if (!newCityStateStringList?.length) {
        return rest;
      }
      return {
        ...rest,
        cityStateStringList: newCityStateStringList,
      };
    });
  }, [options, setModalFilters, isProjectLocationsLoading]);

  const subtitle = useMemo(() => {
    if (!modalFilters?.cityStateStringList?.length) {
      return 'Any';
    }

    return (
      modalFilters?.cityStateStringList
        ?.reduce((acc, cityStateString) => {
          const parsedProjectLocation =
            getParsedProjectLocationFromJsonString(cityStateString);
          if (!parsedProjectLocation) {
            return acc;
          }
          const { city, state } = parsedProjectLocation;
          const cityState = [city, state].filter(Boolean).join(', ');
          if (cityState) {
            acc.push(cityState);
          }
          return acc;
        }, [] as string[])
        ?.join('; ') || 'Any'
    );
  }, [modalFilters?.cityStateStringList]);

  return (
    <FilterCollapse
      title='Project Region'
      subtitle={subtitle}
      content={
        <ProjectLocationFilterContent
          projectLocations={projectLocations}
          isProjectLocationsLoading={isProjectLocationsLoading}
          isProjectLocationsError={isProjectLocationsError}
          setCityStateStringList={setCityStateStringList}
          cityStateStringList={modalFilters?.cityStateStringList || []}
          options={options}
          projectLocationSearchTerm={projectLocationSearchTerm}
          setProjectLocationSearchTerm={setProjectLocationSearchTerm}
          onClearProjectLocationSearchTerm={onClearProjectLocationSearchTerm}
        />
      }
    />
  );
};

type ProjectLocationFilterContentProps = {
  projectLocations: LocationModel[] | undefined;
  isProjectLocationsLoading: boolean;
  isProjectLocationsError: boolean;
  setCityStateStringList: (cityStateStringList: string[]) => void;
  cityStateStringList: string[];
  options: SelectOption[];
  projectLocationSearchTerm: string;
  setProjectLocationSearchTerm: ReactStateUpdater<string>;

  onClearProjectLocationSearchTerm: () => void;
};
const ProjectLocationFilterContent = ({
  projectLocations,
  isProjectLocationsLoading,
  isProjectLocationsError,
  options,
  projectLocationSearchTerm,
  setProjectLocationSearchTerm,
  onClearProjectLocationSearchTerm,
  setCityStateStringList,
  cityStateStringList,
}: ProjectLocationFilterContentProps) => {
  return (
    <StyledContainer>
      <StyledInput
        placeholder='Search project region'
        value={projectLocationSearchTerm}
        onChange={(e) => {
          const value = e.target.value;
          setProjectLocationSearchTerm(value);
          if (!value) {
            onClearProjectLocationSearchTerm();
          }
        }}
        allowClear
      />
      <SearchResults
        isProjectLocationsLoading={isProjectLocationsLoading}
        isProjectLocationsError={isProjectLocationsError}
        projectLocations={projectLocations}
        options={options}
        projectLocationSearchTerm={projectLocationSearchTerm}
        onClearProjectLocationSearchTerm={onClearProjectLocationSearchTerm}
        setCityStateStringList={setCityStateStringList}
        cityStateStringList={cityStateStringList}
      />
    </StyledContainer>
  );
};

type SearchResultsProps = {
  isProjectLocationsLoading: boolean;
  isProjectLocationsError: boolean;
  projectLocations: LocationModel[] | undefined;
  options: SelectOption[];
  projectLocationSearchTerm: string;
  setCityStateStringList: (cityStateStringList: string[]) => void;
  cityStateStringList: string[];

  onClearProjectLocationSearchTerm: () => void;
};
const SearchResults = ({
  isProjectLocationsLoading,
  isProjectLocationsError,
  projectLocations,
  options,
  projectLocationSearchTerm,
  setCityStateStringList,
  cityStateStringList,
  onClearProjectLocationSearchTerm,
}: SearchResultsProps) => {
  const isToggleCollapseState = useMemo(
    () => !!options?.length && options?.length > MAX_PROJECT_LOCATIONS,
    [options?.length]
  );

  const [isCollapsed, setIsCollapsed] = useState(true);

  const selectOptions = useMemo(() => {
    if (!isToggleCollapseState) {
      return options;
    }
    if (isCollapsed) {
      return options?.slice(0, MID_PROJECT_LOCATIONS);
    }
    return options;
  }, [isCollapsed, options, isToggleCollapseState]);

  const toggleCollapseState = useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  if (isProjectLocationsLoading) {
    return (
      <StyledSpinContainer>
        <Spin />
      </StyledSpinContainer>
    );
  }

  if (isProjectLocationsError || !projectLocations) {
    return <div>Error loading project locations</div>;
  }

  if (!options?.length) {
    return (
      <NoLocationsFound
        projectLocationSearchTerm={projectLocationSearchTerm}
        onClearProjectLocationSearchTerm={onClearProjectLocationSearchTerm}
      />
    );
  }

  return (
    <StyledCollapseGroup>
      <StyledCheckGroup
        value={cityStateStringList}
        options={selectOptions}
        onChange={(value) => {
          setCityStateStringList(value as string[]);
        }}
      />
      {isToggleCollapseState && (
        <StyledCollapseButtonContainer>
          <StyledCollapseButton onClick={toggleCollapseState}>
            {isCollapsed ? `Show all (${options?.length})` : 'Show Fewer'}
          </StyledCollapseButton>
        </StyledCollapseButtonContainer>
      )}
    </StyledCollapseGroup>
  );
};

type NoLocationsFoundProps = {
  projectLocationSearchTerm: string;

  onClearProjectLocationSearchTerm: () => void;
};
const NoLocationsFound = ({
  projectLocationSearchTerm,
  onClearProjectLocationSearchTerm,
}: NoLocationsFoundProps) => {
  return (
    <StyledNoLocationsFound>
      <div className='search-icon-section'>
        <div className='search-icon-container'>
          <SearchLoupeQuestion />
        </div>
      </div>
      <div className='title-subtitle'>
        <div className='title'>No matching locations</div>
        {!!projectLocationSearchTerm && (
          <div className='subtitle'>
            Check spelling or try the full city name.
          </div>
        )}
      </div>
      {!!projectLocationSearchTerm && (
        <div
          className='search-icon-section'
          onClick={onClearProjectLocationSearchTerm}
        >
          <StyledShowAllButton>Show all locations</StyledShowAllButton>
        </div>
      )}
    </StyledNoLocationsFound>
  );
};

const StyledCollapseGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (max-width: 768px) {
    gap: 4px;
  }
`;
const StyledFlexContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;
const StyledCollapseButtonContainer = styled(StyledFlexContainer)`
  justify-content: flex-start;
`;

const StyledShowAllButton = styled(UnstyledButton)`
  display: flex;
  align-items: center;
  justify-content: center;

  color: ${({ theme }) => theme.palette.primary[0]};
  font-weight: 600;
  font-size: 12px;
  line-height: 16px;
  padding: 4px 8px;

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 20px;
    padding: 10px 16px;
  }
`;

const StyledCollapseButton = styled(StyledShowAllButton)``;

const StyledNoLocationsFound = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: center;

  .search-icon-section {
    display: flex;
    align-items: center;
    justify-content: center;

    .search-icon-container {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
    }
  }

  .title-subtitle {
    display: flex;
    flex-direction: column;
    color: ${({ theme }) => theme.palette.text[9]};

    .title {
      font-weight: 600;
    }
  }
`;

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 8px 0;
`;

const StyledSpinContainer = styled(StyledFlexContainer)`
  padding: 8px 0 0 0;
`;

const StyledCheckGroup = styled(StyledCheckboxGroup)`
  padding: 0;
`;

const MAX_PROJECT_LOCATIONS = 12;
const DIFF_PROJECT_LOCATIONS = 5;
const MID_PROJECT_LOCATIONS = MAX_PROJECT_LOCATIONS - DIFF_PROJECT_LOCATIONS;
